import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listOpenLiveSessions } from '../liveSessionApi';
import {
  hasOpenSessionWithoutMatch,
  pickJoinTarget,
} from '../lib/pickJoinTarget';
import type { LiveJoinTarget, OpenLiveSession } from '../types';

/** Default lobby poll interval so new sessions appear without a manual refresh. */
export const OPEN_LIVE_SESSION_POLL_MS = 5_000;

export type UseOpenLiveSessionResult = {
  sessions: OpenLiveSession[];
  joinTarget: LiveJoinTarget | null;
  waitingForMatch: boolean;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
};

/**
 * Loads open live sessions for the authenticated user and polls while mounted.
 * @param userId - must match the JWT subject (API enforces self-only)
 * @param preferredTeamId - optional: prefer join targets on this team
 * @param pollIntervalMs - poll period; set 0 to disable polling (tests)
 */
export function useOpenLiveSession(
  userId: string | undefined,
  preferredTeamId?: string | null,
  pollIntervalMs: number = OPEN_LIVE_SESSION_POLL_MS,
): UseOpenLiveSessionResult {
  const [sessions, setSessions] = useState<OpenLiveSession[]>([]);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const hasLoadedRef = useRef(false);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    hasLoadedRef.current = false;
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setSessions([]);
      setLoading(false);
      setError(null);
      hasLoadedRef.current = false;
      return;
    }

    let cancelled = false;

    async function pull(isInitial: boolean) {
      if (isInitial && !hasLoadedRef.current) setLoading(true);
      try {
        const res = await listOpenLiveSessions(userId!);
        if (cancelled) return;
        setSessions(res.sessions ?? []);
        setError(null);
        hasLoadedRef.current = true;
      } catch (err) {
        if (cancelled) return;
        const next = err instanceof Error ? err : new Error(String(err));
        // Keep last good sessions on poll failure; only hard-fail the first load.
        if (!hasLoadedRef.current) {
          setSessions([]);
          setError(next);
        } else {
          setError(next);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    pull(true);

    if (pollIntervalMs <= 0) {
      return () => {
        cancelled = true;
      };
    }

    const id = window.setInterval(() => {
      // Skip ticks while the tab is hidden to reduce noise; catch up on focus via visibility handler.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      pull(false);
    }, pollIntervalMs);

    const onVisible = () => {
      if (document.visibilityState === 'visible') pull(false);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userId, pollIntervalMs, tick]);

  const joinTarget = useMemo(
    () => pickJoinTarget(sessions, preferredTeamId),
    [sessions, preferredTeamId],
  );

  const waitingForMatch = useMemo(
    () => joinTarget == null && hasOpenSessionWithoutMatch(sessions, preferredTeamId),
    [joinTarget, sessions, preferredTeamId],
  );

  return { sessions, joinTarget, waitingForMatch, loading, error, refetch };
}
